import type {
  AnalysisSetup,
  ChatReport,
  KakaoParseErrorCode,
  ParsedChat,
  ParseProgress,
  ParseSummary,
} from './types';

/**
 * Promise wrapper around worker.ts (spec §6.4). The worker keeps the parsed
 * chat, so only the small summary and the final report cross threads. When a
 * module worker is unavailable the same functions run on the main thread.
 */

export type AnalyzerErrorCode = KakaoParseErrorCode | 'NOT_PARSED' | 'INTERNAL';

export type WorkerRequest =
  | { readonly type: 'parse'; readonly id: number; readonly files: readonly File[] }
  | { readonly type: 'analyze'; readonly id: number; readonly setup: AnalysisSetup };

export type WorkerResponse =
  | { readonly type: 'progress'; readonly id: number; readonly progress: ParseProgress }
  | { readonly type: 'parsed'; readonly id: number; readonly summary: ParseSummary }
  | { readonly type: 'report'; readonly id: number; readonly report: ChatReport }
  | { readonly type: 'error'; readonly id: number; readonly code: AnalyzerErrorCode; readonly message: string };

export const INTERNAL_ERROR_MESSAGE = '대화를 분석하다가 문제가 생겼어요. 페이지를 새로고침한 뒤 다시 시도해 주세요.';
export const NOT_PARSED_MESSAGE = '분석할 대화가 없어요. 대화 파일을 다시 골라 주세요.';

export class ChatAnalyzerError extends Error {
  readonly code: AnalyzerErrorCode;

  constructor(code: AnalyzerErrorCode, message: string) {
    super(message);
    this.name = 'ChatAnalyzerError';
    this.code = code;
  }
}

export interface ParseOptions {
  readonly onProgress?: (progress: ParseProgress) => void;
  readonly signal?: AbortSignal;
}

export interface AnalyzeOptions {
  readonly signal?: AbortSignal;
}

export interface ChatAnalyzer {
  /** Parses files and keeps the chat for analyze(). Replaces any earlier chat. */
  parse(files: readonly File[], options?: ParseOptions): Promise<ParseSummary>;
  /** Stats + report card for the kept chat. */
  analyze(setup: AnalysisSetup, options?: AnalyzeOptions): Promise<ChatReport>;
  /** Forgets the parsed chat and cancels pending work (terminates the worker). */
  reset(): void;
  dispose(): void;
  /** 'worker', or 'main' after the fallback. For diagnostics and tests. */
  readonly mode: 'worker' | 'main';
}

export interface ChatAnalyzerOptions {
  /** Return null to use the main-thread fallback. Defaults to the module worker. */
  readonly createWorker?: () => Worker | null;
}

interface PendingRequest {
  readonly request: WorkerRequest;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: ParseProgress) => void;
  resolve(value: unknown): void;
  reject(reason: unknown): void;
  cleanup(): void;
}

export function abortError(): DOMException {
  return new DOMException('분석을 멈췄어요.', 'AbortError');
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'name' in error && (error as { name: unknown }).name === 'AbortError';
}

function createModuleWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null;
  try {
    return new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  } catch {
    return null;
  }
}

/** Converts anything thrown by the parser/pipeline into a ChatAnalyzerError (or an AbortError). */
export function toAnalyzerError(error: unknown): ChatAnalyzerError | DOMException {
  if (error instanceof ChatAnalyzerError) return error;
  if (isAbortError(error)) return abortError();
  if (error instanceof Error && error.name === 'KakaoParseError' && 'code' in error && typeof error.code === 'string') {
    return new ChatAnalyzerError(error.code as KakaoParseErrorCode, error.message);
  }
  return new ChatAnalyzerError('INTERNAL', INTERNAL_ERROR_MESSAGE);
}

function nextTask(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export function createChatAnalyzer(options: ChatAnalyzerOptions = {}): ChatAnalyzer {
  const createWorker = options.createWorker ?? createModuleWorker;
  const pending = new Map<number, PendingRequest>();
  let worker: Worker | null = null;
  let workerResponded = false;
  let workerHasChat = false;
  let useMainThread = false;
  let disposed = false;
  let nextId = 1;
  /** Files of the last successful parse, to rebuild the chat after a worker loss. */
  let lastFiles: readonly File[] | null = null;
  let mainChat: ParsedChat | null = null;

  function rejectAll(reason: () => unknown): void {
    const entries = [...pending.values()];
    pending.clear();
    for (const entry of entries) {
      entry.cleanup();
      entry.reject(reason());
    }
  }

  function dropWorker(): void {
    if (!worker) return;
    worker.onmessage = null;
    worker.onerror = null;
    worker.onmessageerror = null;
    worker.terminate();
    worker = null;
    workerResponded = false;
    workerHasChat = false;
  }

  function handleMessage(response: WorkerResponse): void {
    workerResponded = true;
    const entry = pending.get(response.id);
    if (!entry) return;
    if (response.type === 'progress') {
      entry.onProgress?.(response.progress);
      return;
    }
    pending.delete(response.id);
    entry.cleanup();
    if (response.type === 'parsed') {
      workerHasChat = true;
      entry.resolve(response.summary);
    } else if (response.type === 'report') {
      entry.resolve(response.report);
    } else {
      entry.reject(new ChatAnalyzerError(response.code, response.message || INTERNAL_ERROR_MESSAGE));
    }
  }

  function switchToMainThread(): void {
    dropWorker();
    useMainThread = true;
    const entries = [...pending.values()];
    pending.clear();
    for (const entry of entries) {
      entry.cleanup();
      runOnMainThread(entry.request, entry.onProgress, entry.signal).then(entry.resolve, entry.reject);
    }
  }

  function ensureWorker(): Worker | null {
    if (useMainThread) return null;
    if (worker) return worker;
    const created = createWorker();
    if (!created) {
      useMainThread = true;
      return null;
    }
    worker = created;
    workerResponded = false;
    workerHasChat = false;
    created.onmessage = (event: MessageEvent<WorkerResponse>) => handleMessage(event.data);
    created.onerror = (event: Event) => {
      event.preventDefault();
      if (!workerResponded) {
        // The module worker never started (old browser, blocked script): run here instead.
        switchToMainThread();
        return;
      }
      dropWorker();
      rejectAll(() => new ChatAnalyzerError('INTERNAL', INTERNAL_ERROR_MESSAGE));
    };
    created.onmessageerror = () => {
      dropWorker();
      rejectAll(() => new ChatAnalyzerError('INTERNAL', INTERNAL_ERROR_MESSAGE));
    };
    return created;
  }

  async function runOnMainThread(request: WorkerRequest, onProgress?: (progress: ParseProgress) => void, signal?: AbortSignal): Promise<unknown> {
    try {
      if (request.type === 'parse') {
        mainChat = null;
        const parser = await import('./parser/index');
        const chat = await parser.parseKakaoExport(request.files, { onProgress, signal });
        if (signal?.aborted) throw abortError();
        mainChat = chat;
        return parser.summarizeParsedChat(chat);
      }
      if (!mainChat && lastFiles) {
        const parser = await import('./parser/index');
        mainChat = await parser.parseKakaoExport(lastFiles, { signal });
      }
      if (!mainChat) throw new ChatAnalyzerError('NOT_PARSED', NOT_PARSED_MESSAGE);
      const { analyzeChat } = await import('./pipeline');
      await nextTask(); // let the "채점하는 중" screen paint before the blocking work
      if (signal?.aborted) throw abortError();
      return analyzeChat(mainChat, request.setup);
    } catch (error) {
      throw toAnalyzerError(error);
    }
  }

  function send(request: WorkerRequest, signal?: AbortSignal, onProgress?: (progress: ParseProgress) => void): Promise<unknown> {
    if (disposed || signal?.aborted) return Promise.reject(abortError());
    const target = ensureWorker();
    if (!target) return runOnMainThread(request, onProgress, signal);
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        if (!pending.has(request.id)) return;
        // Terminating is the only way to stop a busy worker; the kept chat goes with it.
        dropWorker();
        rejectAll(abortError);
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      pending.set(request.id, {
        request,
        signal,
        onProgress,
        resolve,
        reject,
        cleanup: () => signal?.removeEventListener('abort', onAbort),
      });
      target.postMessage(request);
    });
  }

  return {
    async parse(files, parseOptions = {}) {
      const request: WorkerRequest = { type: 'parse', id: nextId++, files: [...files] };
      lastFiles = null;
      const summary = await send(request, parseOptions.signal, parseOptions.onProgress) as ParseSummary;
      lastFiles = request.files;
      return summary;
    },
    async analyze(setup, analyzeOptions = {}) {
      if (!disposed && !useMainThread && !workerHasChat && lastFiles) {
        // The worker that held the chat is gone (crash or cancel): parse again first.
        await send({ type: 'parse', id: nextId++, files: lastFiles }, analyzeOptions.signal);
      }
      return await send({ type: 'analyze', id: nextId++, setup }, analyzeOptions.signal) as ChatReport;
    },
    reset() {
      dropWorker();
      mainChat = null;
      lastFiles = null;
      rejectAll(abortError);
    },
    dispose() {
      this.reset();
      disposed = true;
    },
    get mode() {
      return useMainThread ? 'main' : 'worker';
    },
  };
}
